import _get from 'lodash/get';
import _kebabCase from 'lodash/kebabCase';
import _isEmpty from 'lodash/isEmpty';
import { getWalletPixallMetadata } from 'reaxl-analytics';
import { Pixall, Google } from '../analytics/index.js';
import { addListingTriggerData, getInventoryOwnerById, isPremiumSpotlight, isVDP } from '../utilities/index.js';
import { DataLayerTransformer, productConfigTransformer, inventoryTransformer as pixallInventoryTransformer } from '../transformers/pixall/index.js';

// helper method to fire common GA event when wallet modal is opened.
import { myWalletClickHelper, sanitizeValue } from './helpers/index.js';
import { inventoryTransformer as googleInventoryTransformer } from '../transformers/google/index.js';
import { getCDLVehicleTransformer } from '../transformers/pixall/inventoryTransformer';
import { getConsumerMetadata } from '../transformers/pixall/DataLayerTransformer';
import valueEventCode from '../constants/valueEventCode.js';
const labelsMap = {
  'mark-as-done': 'mark_done',
  'reschedule-test-drive': 'resched_test_drv',
  'trade-in-value': 'edt_trd_val',
  'cash-down': 'edt_cash_dwn',
  'loan-terms': 'edt_term',
  'credit-rating': 'edt_crd_rgt',
  'edit-vehicle-protections': 'edt_v_prtect',
  'schedule-a-test-drive': 'sched_test_drv',
  'apply-for-credit': 'apply_crd',
  'view-deal-dashboard': 'vw_dl_dashboard',
  'schedule-test-drive': 'text-link::schedule-test-drive',
  'add-vehicle-protections': 'text-link::add-vehicle-protections',
  'schedule-another-visit': 'text-link::schedule-another-visit',
  'add-down-payment': 'text-link::add-down-payment'
};
const panelMap = {
  tradeIn: {
    cmpName: 'trd_in',
    gaCopy: 'trade-in',
    panelName: 'trade in',
    friendlyPageName: 'My Wallet Trade In'
  },
  kbbValuation: {
    cmpName: 'trd_in',
    gaCopy: 'trade-in',
    panelName: 'trade in',
    friendlyPageName: 'My Wallet Trade In'
  },
  downPayment: {
    cmpName: 'dwn_pymt',
    gaCopy: 'down-payment',
    panelName: 'down payment',
    friendlyPageName: 'My Wallet Down Payment'
  },
  creditScore: {
    cmpName: 'crd_fin',
    gaCopy: 'credit-terms',
    panelName: 'credit & finance',
    friendlyPageName: 'My Wallet Credit & Finance'
  },
  budget: {
    cmpName: 'my_bgt',
    gaCopy: 'budget',
    panelName: 'my budget',
    friendlyPageName: 'my wallet my budget'
  },
  valuationHistory: {
    panelName: 'trade in',
    friendlyPageName: 'My Wallet Trade In'
  }
};
const inputsMap = {
  tradeInValue: {
    cmp: 'estm_trd_val',
    gaCopy: 'trade-in-value',
    currentPage: 'modal: my wallet trade in',
    pageName: 'modal: my wallet trade in'
  },
  downPaymentAmount: {
    cmp: 'dwn_pymt',
    gaCopy: 'down-payment',
    currentPage: 'My Wallet Down Payment',
    pageName: 'My Wallet Down Payment'
  },
  termLength: {
    cmp: 'term_lmt',
    gaCopy: 'term',
    pageName: 'modal: my wallet credit & finance',
    currentPage: 'modal: my wallet credit & finance'
  },
  creditType: {
    cmp: 'crd_rtg',
    gaCopy: 'credit-rating',
    pageName: 'modal: my wallet credit & finance',
    currentPage: 'modal: my wallet credit & finance'
  },
  amountOwed: {
    cmp: 'amt_owd_trd',
    gaCopy: 'amount-owed',
    currentPage: 'modal: my wallet trade in',
    pageName: 'modal: my wallet trade in'
  },
  budgetAmountInput: {
    cmp: 'chg_bdgt_amt',
    gaCopy: 'chg_bdgt_amt'
  },
  budgetType: {
    cmp: 'bdgt',
    gaCopy: 'bdgt - '
  },
  leaseMileageRange: {
    cmp: 'ml_yr',
    pageName: 'modal: my wallet credit & finance',
    currentPage: 'modal: my wallet credit & finance',
    gaCopy: 'my-wallet_miles-per-year'
  }
};
const walletPixallFields = {
  feature: 'MyWallet',
  eventSource: 'userAccount',
  hasListingType: false
};
const listingTypeMapping = {
  alp: 'alp',
  compare: 'lstg',
  listings: 'lstg',
  recommendations: 'recommendations',
  spotlight: 'spv',
  supplemental: 'sup'
};

/**
 * get eventData by pageId
 */
const getEventData = (pageId, clickType, paginatedListingIndex, inventory, dataLayer) => {
  let eventData;
  if (pageId === 'fyc_srl') {
    eventData = {
      vehicle: {
        clickType,
        listingPosition: paginatedListingIndex
      }
    };
  } else {
    eventData = {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    };
  }
  return eventData;
};
const getWalletMetaData = async paymentsInfo => {
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo);
  let consumerMetadata = {};
  const metaData = await getConsumerMetadata();
  if (!_isEmpty(metaData)) {
    consumerMetadata = metaData;
    consumerMetadata.walletId = paymentsInfo?.walletId;
  }
  return {
    consumerMetadata,
    dealMetadata,
    tradeinMetadata
  };
};
const getOffsitePixallData = async function (data, paymentsDuck, state, impression) {
  if (data === void 0) {
    data = {};
  }
  if (impression === void 0) {
    impression = false;
  }
  const {
    location,
    inventoryId,
    filterValues
  } = data;
  let pixallData;
  switch (location) {
    case 'my-wallet':
      {
        const paymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
        const walletMetaData = await getWalletMetaData(paymentsInfo);
        pixallData = {
          ...walletPixallFields,
          ...walletMetaData
        };
        break;
      }
    case 'nds':
      {
        const inventory = getInventoryOwnerById(state, inventoryId);
        pixallData = {
          feature: 'nds',
          eventSource: 'application',
          filterValues,
          clickType: inventory?.clickType,
          pixallData: {
            clickTypeToListingType: true
          }
        };
        if (impression) {
          pixallData = {
            ...pixallData,
            ...pixallInventoryTransformer.getInventoryData(inventory)
          };
        } else {
          pixallData.inventory = inventory;
        }
        break;
      }
    case 'srp':
      pixallData = {
        eventSource: 'application',
        hasListingType: false,
        filterValues
      };
      break;
    case 'vdp':
      {
        const inventory = getInventoryOwnerById(state, inventoryId);
        pixallData = {
          eventSource: 'application',
          inventory,
          pixallData: {
            clickTypeToListingType: true
          }
        };
        break;
      }
    default:
      pixallData = {};
  }
  return pixallData;
};
export async function myWalletOpenPageLoad(_ref, option) {
  let {
    dataIsland,
    dataLayer,
    history
  } = _ref;
  await Pixall.sendPageEvent({
    ...walletPixallFields,
    pageType: dataIsland?.page?.pixallPageType || dataLayer?.page?.pageType,
    label: 'my-wallet',
    isWalletPageload: true
  }, {
    dataLayer: {
      ...dataLayer,
      page: {
        ...dataLayer.page,
        siteSection: 'my-wallet',
        pageType: 'my-wallet',
        detailPageName: 'my-wallet'
      }
    },
    history
  }, option);
  return {
    cmp: 'my-wallet'
  };
}

/**
 * @summary Sends a Google and Pixall clickEvent for wallet toggle in filter rail
 * @cmp my_walt_on, my_walt_off
 * @events event1
 * Required: option { toggleOn } * boolean value
 */
export async function myWalletFilterToggleClick(_ref2, option) {
  let {
    data = {},
    dataLayer
  } = _ref2;
  const {
    eventResult
  } = data;
  const toggleStatus = data.toggleOn ? '_on' : '_off';
  const cmp = 'my_walt' + toggleStatus;
  // NOTE: Per spec, only fire GA event if toggled on and toggle is not already on the modal
  if (!data.isModal && data.toggleOn) {
    myWalletClickHelper({
      eventResult
    }, option);
  }
  Pixall.sendClickEvent({
    ...walletPixallFields,
    label: cmp
  }, dataLayer, option);
  return {
    cmp
  };
}

/**
 * @summary Sends Google and Pixall clickEvent for My Wallet Monthly Payment box in the price filter
 * @cmp set_bdgt, upd_bdgt
 * @events event1
 */
export function myWalletMonthlyPaymentFilterClick(_ref3, option) {
  let {
    data = {},
    dataLayer = {}
  } = _ref3;
  const {
    eventResult,
    cmp
  } = data;
  myWalletClickHelper({
    eventResult
  }, option);
  Pixall.sendClickEvent({
    ...walletPixallFields,
    label: eventResult
  }, dataLayer, option);
  return {
    cmp
  };
}

/**
 * @summary Sends a Pixall click event for 'See Results' button in Search By Budget section of
 * WalletCarousel
 * @cmp see_rslt
 * @events event1
 */
export async function myWalletSearchByBudgetClick(_ref4, option) {
  let {
    dataLayer
  } = _ref4;
  const cmp = 'see_rslt';
  Pixall.sendClickEvent({
    ...walletPixallFields,
    label: cmp
  }, dataLayer, option);
  return {
    cmp
  };
}

/**
 * @summary Sends a Google and Pixall clickEvent for the '4 square' summary page buttons
 * @cmp walt_...
 * @events event1
 */
export function myWalletSummaryLinkClick(_ref5, option) {
  let {
    data = {},
    dataLayer,
    state = {}
  } = _ref5;
  const {
    panel,
    isModal,
    inventoryId,
    isPsxVdp
  } = data;
  const {
    cmpName,
    gaCopy
  } = panelMap[panel] || {};
  const cmp = 'walt_' + cmpName;
  const label = cmpName === 'my_bgt' ? 'walt_mnth_bdgt' : cmp;
  // NOTE: Tiles in wallet modal only pass the copy without `tile::open::`
  const eventResult = !isModal ? `tile::open::${gaCopy}` : gaCopy;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const {
    specifications
  } = inventory;
  const fuelEconomy = specifications?.mpg?.value;

  // Override some pixall data if is psx page
  const psxPixallData = {
    listingType: 'pvt',
    pageId: 'fyc_psx_vdp'
  };
  myWalletClickHelper({
    eventResult
  }, option);
  Pixall.sendClickEvent({
    inventory,
    ...walletPixallFields,
    label,
    fuelEconomy,
    ...(isVDP(dataLayer) ? {
      listingType: 'alp'
    } : {
      hasListingType: false
    }),
    ...(isPsxVdp && {
      ...psxPixallData
    }),
    ...(isVDP(dataLayer) && {
      cdl: {
        eventProperties: {
          vehicle: pixallInventoryTransformer.getVehicleCommonDataLayer(inventory)
        }
      }
    })
  }, dataLayer, option);
  return {
    cmp
  };
}

/**
 * @summary Sends a Pixall click event for the "Calculate Payments" button inside the wallet grid
 * @cmp upd_walt
 * @events event1,event74
 */
export function myWalletCalculatePaymentsCTAClick(_ref6, option) {
  let {
    data = {},
    dataLayer
  } = _ref6;
  const {
    label
  } = data;
  const payload = {
    cmp: 'upd_pymt_set',
    co_txt_url: label.trim()
  };
  Pixall.sendClickEvent({
    ...walletPixallFields,
    label: payload.cmp
  }, dataLayer, option);
  myWalletClickHelper({
    eventResult: `cta::open::${_kebabCase(label)}`
  }, option);
  return payload;
}

/**
 * @summary Sends a Google and Pixall clickEvent for 'Get Started' button in My Wallet Intro module (AT HP Redesign)
 */
export async function myWalletGetStartedClick(_ref7, option) {
  let {
    dataLayer
  } = _ref7;
  Pixall.sendClickEvent({
    ...walletPixallFields,
    label: 'get_start'
  }, dataLayer, option);
  myWalletClickHelper({
    eventResult: 'cta::open::get-started'
  }, option);
  return {};
}

/**
 * @summary sends a Google and Pixall impression event for Non-Deal Notifications
 */
export function myWalletNonDealNotificationsImpression(metaData, option) {
  const {
    data = {}
  } = metaData;
  const {
    textContent: rawTextContent = ''
  } = data;
  const textContent = sanitizeValue(rawTextContent);
  Pixall.sendImpression(metaData, {
    ...walletPixallFields,
    label: labelsMap[textContent],
    eventType: 'view'
  }, option);
  Google.sendImpressions(metaData, {
    eventElement: 'my-wallet',
    eventResult: `text-link::${textContent}`
  }, option);
  return {};
}

/**
 * @summary sends a Google and Pixall click event for Non-Deal Notifications Click US1075353
 */

export function myWalletNonDealNotificationsClick(_ref8, option) {
  let {
    data = {},
    dataLayer = {}
  } = _ref8;
  const {
    textContent: rawTextContent = '',
    vehicle = {}
  } = data;
  const textContent = sanitizeValue(rawTextContent);
  Pixall.sendClickEvent({
    ...walletPixallFields,
    label: labelsMap[textContent]
  }, dataLayer, option);
  myWalletClickHelper({
    eventElement: 'my-wallet',
    eventResult: `text-link::${textContent}`,
    eventData: {
      vehicle: googleInventoryTransformer(vehicle, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  return {};
}
/**
 * @summary Sends a Google clickEvent and Adobe link track (and search criteria value from dropdown) for the 'Continue' button in Browse by Budget module
 * @cmp upd_walt_bdgt
 * @events event1, event74
 */
export async function browseByBudgetWalletContinueClick(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    data,
    dataLayer
  } = metadata;
  const {
    withPixallData
  } = data;
  myWalletClickHelper({
    eventResult: 'cta::open::continue',
    dataLayer,
    withPixallData,
    pixallLabel: 'cta::open::continue'
  }, option);
  return {
    cmp: 'upd_walt_bdgt'
  };
}

/**
 * @summary Sends Adobe/PIXALL link track for the wallet payment ribbon on the pricing snapshot @ the top of the VDP
 * @cmp upd_walt
 * @events event1
 */
export function snapshotPaymentDetailsClick(_ref9, option) {
  let {
    data = {},
    dataLayer = {},
    state
  } = _ref9;
  const {
    suffix = '',
    clickType = '',
    paginatedListingIndex = '',
    parentId = ''
  } = data;
  const cmp = `see_dtl${suffix}`;
  const {
    inventoryId,
    par
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const pageId = DataLayerTransformer.getPageId(dataLayer);
  const pageName = _get(dataLayer, 'page.pageName', '');
  let eventResult;
  let eventData;
  let eventElement;
  const isCompare = par === 'comp_lstg';
  const isPreorder = par === 'preorder';
  const isInline = par === 'inline';
  const listingType = isPremiumSpotlight(parentId) ? 'highlight' : null;
  let label;
  switch (par) {
    case 'alp':
      eventResult = 'text-link::see-details';
      eventData = {
        vehicle: {
          clickType: 'alpha',
          listingPosition: 0
        }
      };
      label = cmp;
      break;
    case 'comp_lstg':
      eventElement = 'compare';
      eventResult = 'text-link::open::see-details';
      eventData = getEventData(pageId, clickType, paginatedListingIndex, inventory, dataLayer);
      label = 'see_dtl - compare';
      break;
    case 'preorder':
      {
        eventElement = 'my-wallet';
        eventResult = 'text-link::open::see-details';
        eventData = {
          vehicle: {
            clickType: 'newcarcustomorder'
          }
        };
        break;
      }
    case 'buildAndPrice':
      {
        eventElement = 'my-wallet';
        eventResult = 'text-link::open::see-details';
        const {
          bodyStyle,
          catalog,
          makeId,
          makeName,
          modelId,
          modelName,
          series,
          seriesId
        } = googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        });
        eventData = {
          vehicle: {
            clickType: 'newcarcustomorder',
            bodyStyle,
            catalog,
            makeId,
            makeName,
            modelId,
            modelName,
            series,
            seriesId
          }
        };
        break;
      }
    case 'inline':
      {
        eventElement = 'my-wallet';
        eventResult = 'text-link::open::see-details::inline';
        eventData = getEventData(pageId, clickType, paginatedListingIndex, inventory, dataLayer);
        break;
      }
    default:
      eventElement = 'my-wallet';
      eventResult = 'text-link::open::see-details';
      eventData = {
        vehicle: {
          clickType,
          listingPosition: paginatedListingIndex
        }
      };
      label = cmp;
      break;
  }
  if (isPreorder) {
    Pixall.sendClickEvent({
      label: eventResult,
      eventSource: 'userAccount',
      feature: 'MyWallet',
      listingType: 'newcarorder'
    }, dataLayer, option);
  } else if (!isInline) {
    Pixall.sendClickEvent({
      cdl: addListingTriggerData(parentId, {
        eventProperties: {}
      }),
      label,
      eventSource: 'vehicle',
      listingCode: ownerId,
      feature: 'MyWallet',
      inventory,
      par,
      // add this to all events?
      listingType,
      ...(isCompare && pageName === 'srp' && {
        listingType: 'compare'
      }),
      pixallData: {
        clickTypeToListingType: isVDP(dataLayer)
      },
      ...(inventory?.owner?.privateSeller && {
        pageId: 'fyc_psx_vdp'
      })
    }, dataLayer, option);
  }
  myWalletClickHelper({
    eventResult,
    eventElement,
    eventData
  }, option);
  return {
    cmp,
    par
  };
}

/**
 * @summary Sends a Google and PixAll click event for the wallet payment ribbon on the pricing snapshot @ the top of the VDP
 * @cmp upd_walt
 * @events event1
 */
export function paymentDetailsMakeChangesClick(_ref0, option) {
  let {
    data = {},
    dataLayer = {},
    state
  } = _ref0;
  const {
    suffix = '',
    label
  } = data;
  const cmp = label || `my_walt${suffix}`;
  const {
    inventoryId,
    par,
    listingCategory
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const pageName = _get(dataLayer, 'page.pageName', '');
  const payload = {
    cmp,
    co_txt_url: 'Make Changes',
    par
  };
  const listingType = listingTypeMapping[listingCategory] || '';
  if (pageName === 'bap') {
    const pixallBapPayload = {
      label: 'my_walt',
      inventory,
      feature: 'MyWallet',
      eventSource: 'userAccount'
    };
    Pixall.sendClickEvent({
      ...pixallBapPayload
    }, dataLayer, option);
    Google.sendClickEvent({
      eventResult: 'cta::make-changes'
    }, option);
    return {
      cmp: 'my_walt'
    };
  }
  myWalletClickHelper({
    eventResult: 'cta::make-changes'
  }, option);
  Google.sendPageDataEvent({
    dataLayer: {
      ...dataLayer,
      page: {
        siteSection: 'my-wallet',
        pageType: 'my-wallet',
        detailPageName: 'my-wallet'
      }
    }
  }, option);
  Pixall.sendClickEvent({
    label: cmp,
    inventory,
    feature: 'MyWallet',
    eventSource: 'userAccount',
    listingType,
    pixallData: {
      clickTypeToListingType: !listingType && isVDP(dataLayer)
    }
  }, dataLayer, option);
  return payload;
}

/**
 * @summary Sends a Google click event for links on the trade in panels
 * @cmp n_car_fnd || g_estm_trd_val
 * @events event1
 */
export async function myWalletTradeInClickLink(_ref1, option) {
  let {
    data = {},
    dataLayer = {},
    state
  } = _ref1;
  const {
    isIco = false,
    isWalletPromoCard = false,
    paymentsDuck
  } = data;
  const paymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
  const {
    consumerMetadata,
    dealMetadata,
    tradeinMetadata
  } = await getWalletMetaData(paymentsInfo);
  const eventResult = isIco ? 'text-link::my-wallet::didn\'t-find-your-car' : 'text-link::my-wallet::get-an-estimated-kelley-blue-book-trade-in-value';
  myWalletClickHelper({
    ...(!isWalletPromoCard && {
      eventResult
    }),
    withPixallData: true,
    pixallLabel: `tile::my-wallet::get-an-estimated-kelley-blue-book-trade-in-value${isWalletPromoCard ? '::inline' : ''}`,
    dataLayer,
    pixallData: {
      consumerMetadata,
      dealMetadata,
      tradeinMetadata
    }
  }, option);
  return {
    cmp: isIco ? 'n_car_fnd' : 'g_estm_trd_val'
  };
}

/**
 * @summary Sends a Google click event for ICO links in trade in panel
 * @cmp find_prv_ofr
 */
export async function myWalletIcoLinkClick(_ref10, option) {
  let {
    data = {},
    dataLayer = {},
    state
  } = _ref10;
  const {
    eventResult,
    cmp,
    withPixallData = false,
    paymentsDuck
  } = data;
  const paymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo);
  let consumerMetadata = {};
  const metaData = await getConsumerMetadata();
  if (!_isEmpty(metaData)) {
    consumerMetadata = metaData;
    consumerMetadata.walletId = paymentsInfo?.walletId;
  }
  myWalletClickHelper({
    eventResult,
    dataLayer,
    withPixallData,
    pixallLabel: 'text-link::my-wallet::get-a-kelley-blue-book-instant-cash-offer',
    pixallData: {
      dealMetadata,
      tradeinMetadata,
      consumerMetadata
    }
  }, option);
  return {
    cmp
  };
}

/**
 * @summary Sends an Google click event for the 'Get Trade-in Value' buttons
 * @cmp g_trd_val
 * @events event1
 */
export function myWalletTradeInSubmit(_ref11, option) {
  let {
    data = {},
    dataLayer = {}
  } = _ref11;
  const {
    dealMetadata,
    consumerMetadata,
    tradeinMetadata
  } = data;
  myWalletClickHelper({
    eventResult: 'tile::my-wallet::get-trade-in-value',
    dataLayer,
    withPixallData: true,
    pixallLabel: 'cta::my-wallet::get-trade-in-value',
    dealMetadata,
    consumerMetadata,
    tradeinMetadata
  }, option);
  return {
    cmp: 'g_trd_val'
  };
}

/**
 * @summary Sends a Google click event for the 'Search Available Vehicles' input
 * @cmp srch_v_my_walt...
 * @events event1
 */
export function myWalletSearchAvailableVehiclesSubmit(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  myWalletClickHelper({
    eventResult: 'search-available-vehicles'
  }, option);
  return {
    cmp: 'srch_v_my_walt'
  };
}

/**
 * @summary Sends an PixAll and Google click event for the MyWallet inputs
 * @events event1
 */
export async function myWalletInputChange(_ref12, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref12;
  const {
    inputName,
    paymentsDuck,
    walletPromoCardInput = false
  } = data;
  const extraData = data.extraData || '';
  let targetValue = data.targetValue ? `::${data.targetValue}` : '';
  const paymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
  const {
    consumerMetadata,
    dealMetadata,
    tradeinMetadata
  } = await getWalletMetaData(paymentsInfo);
  if (inputName === 'budgetType') {
    targetValue = data.targetValue;
  }
  const responseData = {
    ...inputsMap[inputName],
    pageType: 'myWallet'
  };
  Pixall.sendClickEvent({
    ...walletPixallFields,
    eventSource: 'userAccount',
    label: inputsMap[inputName].cmp + extraData + (walletPromoCardInput ? '::inline' : ''),
    ...(walletPromoCardInput ? {
      feature: 'myWallet',
      dealMetadata,
      consumerMetadata,
      tradeinMetadata
    } : {
      pxamMessageType: 'pxamContentEvent'
    })
  }, dataLayer, option);
  if (!walletPromoCardInput) {
    myWalletClickHelper({
      eventResult: inputsMap[inputName].gaCopy + targetValue
    }, option);
  }
  return responseData;
}

/**
 * @summary Sends an Google link track for the initial MyWallet interaction (input changes) so that this event only fires once
 */
export async function myWalletInteractionClick(_ref13, option) {
  let {
    data = {},
    dataLayer = {},
    state
  } = _ref13;
  const {
    clickType,
    inventoryId,
    paymentsDuck,
    taggingData,
    isNDS
  } = data;
  const paymentsInfo = paymentsDuck?.selectors?.getPaymentsInfo(state);
  const vehicleData = {
    productConfiguration: 'my-wallet'
  };

  // Custom Dimension 135 - clickType
  // clickType exists when we navigate from other pages to VDP
  if (clickType) {
    vehicleData.clickType = clickType;
  }
  const inventory = getInventoryOwnerById(state, inventoryId);
  Google.sendClickEvent({
    eventElement: 'my-wallet',
    eventResult: 'pencil::my-wallet',
    eventData: {
      vehicle: vehicleData
    }
  }, option);
  const {
    getProductConfiguration
  } = productConfigTransformer;
  const metaData = await getConsumerMetadata();
  const userModified = _get(taggingData, 'userModified', '');
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, userModified);
  Pixall.sendClickEvent({
    calculatorType: 'payment',
    eventType: 'step',
    feature: 'MyWallet',
    eventSource: 'calculator',
    label: isNDS ? 'nds_pencil' : 'mywallet_pencil',
    hasListingType: false,
    cdl: {
      eventProperties: {
        vehicle: {
          productConfiguration: getProductConfiguration(inventory)
        },
        event: {
          valueEventCode: 'D8SH'
        }
      }
    },
    consumerMetadata: !_isEmpty(metaData) ? metaData : {},
    dealMetadata,
    tradeinMetadata
  }, dataLayer, option);
  return {};
}

/**
 * @summary Sends a Google and Pixall tag for the Return to Vehicle Details link on the wallet dashboard on the VDP
 */
export function myWalletReturnToVdpCtaClick(_ref14, option) {
  let {
    data = {},
    dataLayer = {}
  } = _ref14;
  const {
    ctaType
  } = data;
  let gtaLabel = '::return-to-vehicle-details';
  let pixallLabel = 'rtn_v_dtls_';
  if (ctaType === 'link') {
    gtaLabel = 'text-link' + gtaLabel;
    pixallLabel += 'lnk';
  } else if (ctaType === 'button') {
    gtaLabel = 'cta' + gtaLabel;
    pixallLabel += 'btn';
  }
  Google.sendClickEvent({
    eventElement: 'my-wallet',
    eventResult: gtaLabel
  }, option);
  Pixall.sendClickEvent({
    eventType: 'click',
    feature: 'MyWallet',
    eventSource: 'userAccount',
    label: pixallLabel
  }, dataLayer, option);
  return {};
}

/**
 * @summary Sends a GA360 event for the MyWallet/Show Payments button click in the mobile toolbar
 * @cmp my_walt_btn
 * @events event1
 */
export function myWalletMobileToolbarClick(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const pixallData = {
    cdl: {
      eventProperties: {}
    }
  };
  myWalletClickHelper({
    withPixallData: true,
    pixallLabel: 'my_walt',
    pixallData,
    ...metadata
  }, option);
  return {};
}

/**
 * @summary Sends a Google click event for the 'Set Your Budget' click
 * @cmp set_budgt || srch_avl_v
 * @events event1
 */
export function myWalletCarouselBudgetClick(_ref15, option) {
  let {
    data = {}
  } = _ref15;
  const {
    budget
  } = data;
  const cmp = budget ? 'srch_avl_v' : 'set_bdgt';
  if (budget) {
    myWalletClickHelper({
      eventResult: 'search-available-vehicles'
    }, option);
  }
  return {
    cmp
  };
}

/**
 * @summary Sends a Google event for payment details VDP CTA click
 * @cmp ctn_v_dtls
 * @events event1
 */
export async function paymentDetailsVehicleCTAClick(_ref16, option) {
  let {
    data = {},
    dataLayer = {},
    state = {}
  } = _ref16;
  const {
    inventoryId,
    listingCategory
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const listingType = listingTypeMapping[listingCategory] || '';

  // These events will be fired when user click on a link
  // so we need to wait for them to be fullfil before navigate to a the new url to ensure the requests are not canceled
  await Promise.all([Pixall.sendClickEvent({
    eventSource: 'userAccount',
    feature: 'MyWallet',
    label: 'vw_v_dtls_walt',
    inventory,
    listingType,
    pixallData: {
      clickTypeToListingType: !listingType && isVDP(dataLayer)
    }
  }, dataLayer, option),
  // add additional car data for eVar128 and eVar133 if on the SRP (does not populate automatically)
  Google.sendClickEvent({
    eventElement: 'dealer',
    eventResult: 'text-link::continue-to-vehicle-details',
    eventData: {
      vehicle: {
        dealershipId: ownerId.toString()
      }
    }
  }, option)]);
  return {
    cmp: 'ctn_v_dtls'
  };
}

/**
 * @summary Sends a Google event for Apply to Wallet CTA
 * @cmp apply_val
 * @events event1
 */
export function applyValueToWalletClick(_ref17, option) {
  let {
    data = {},
    dataLayer,
    event
  } = _ref17;
  const {
    consumerMetadata,
    dealMetadata,
    tradeinMetadata
  } = data;
  myWalletClickHelper({
    eventResult: 'cta::apply-value-to-wallet'
  }, option);
  Pixall.sendClickEvent({
    feature: 'MyWallet',
    pxamMessageType: 'pxamContentEvent',
    consumerMetadata,
    dealMetadata,
    tradeinMetadata
  }, dataLayer, option);
  return {
    cmp: 'apply_val'
  };
}

/**
 * @summary Sends Google Impressions event when KBB Trade In Indicator is present in Wallet
 * @events event1
 */
export function myWalletKbbTradeInIndicator(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    dataLayer = {}
  } = metadata;
  Google.sendImpressions(metadata, {
    eventElement: 'my-wallet',
    eventResult: 'trade-in-indicator',
    eventNonInteractive: false,
    eventData: {
      vehicle: {}
    }
  }, option);
  Pixall.sendImpression(metadata, {
    eventSource: 'userAccount',
    eventType: 'impression',
    feature: 'MyWallet',
    fullURL: window.location.href,
    label: 'trade-in-indicator',
    pageId: DataLayerTransformer.getPageId(dataLayer),
    pageType: dataLayer?.page?.pageType,
    hasListingType: false
  }, option);
  return {};
}

/**
 * @summary Sends a Google Impressions event when deal banner is present
 * @events event 42
 */
export function myWalletDealBannerImpression(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    data = {},
    dataLayer = {},
    state
  } = metadata;
  const {
    inventoryId
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  Google.sendImpressions(metadata, {
    eventElement: 'my-wallet-deals',
    eventResult: 'deal-created',
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  Pixall.sendImpression(metadata, {
    includeInventory: true,
    inventory,
    ...walletPixallFields,
    label: 'deal-created',
    eventType: 'view',
    pageType: 'vdp',
    vehicleType: (inventory?.listingType || '').toUpperCase(),
    hasListingType: isVDP(dataLayer),
    clickType: dataLayer?.vehicle?.clickType,
    cdl: {
      eventProperties: {
        vehicle: getCDLVehicleTransformer(googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        }))
      }
    }
  }, option);
  return {};
}

/**
 * @summary Sends a Google event for 'View Your Deal Status' CTA
 * @events event 43
 */
export function myWalletViewDealStatusCTAClick(_ref18, option) {
  let {
    data = {},
    dataLayer = {},
    event,
    state = {}
  } = _ref18;
  if (option === void 0) {
    option = {};
  }
  const {
    inventoryId,
    placement = ''
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const textContent = (_get(event, 'target.innerText', '') || _get(event, 'target.textContent', '')).trim();
  const linkCopy = _kebabCase(textContent);
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const vehicle = googleInventoryTransformer(inventory, {
    target: 'vehicle',
    dataLayer
  });
  Pixall.sendClickEvent({
    inventory,
    label: `vw_dl_status - ${placement === 'receipt' ? 'deal receipt' : 'price card'}`,
    eventSource: 'userAccount',
    feature: isAccelerate ? 'dr3' : 'caca',
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    },
    cdl: {
      eventProperties: {
        vehicle: getCDLVehicleTransformer(vehicle)
      }
    }
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'my-wallet-deals',
    eventResult: `text-link::${linkCopy}::${placement}`,
    eventNonInteractive: true,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  return {};
}

/**
 * @summary Sends a Google and Pixall events for active deal CTAs on SRP listing post deal
 */
export function myWalletCheckDealStatusCTAClick(_ref19, option) {
  let {
    data = {},
    dataLayer = {},
    state = {},
    event
  } = _ref19;
  if (option === void 0) {
    option = {};
  }
  const {
    inventoryId,
    filterValues
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const {
    specifications
  } = inventory;
  const exteriorColor = specifications?.color?.value.toUpperCase();
  const fuelEconomy = specifications?.mpg?.value;
  const textContent = (_get(event, 'target.innerText', '') || _get(event, 'target.textContent', '')).trim();
  const linkCopy = textContent.replace(/\s+/g, '-').replace(/[^\w-]/g, '').toLowerCase();
  const label = linkCopy === 'see-vehicle-details' ? 'see_v_delts' : 'chk_status';
  const eventResult = linkCopy === 'check-status' ? `text-link::open::${linkCopy}` : `text-link::${linkCopy}`;
  Pixall.sendClickEvent({
    label,
    eventSource: 'vehicle',
    feature: 'MyWallet',
    inventory,
    filterValues,
    exteriorColor,
    fuelEconomy
  }, dataLayer, option);
  myWalletClickHelper({
    eventResult
  }, option);
  return {};
}

/**
 * @summary Sends a Google clickEvent for the 'Set My Budget' button in SuperHero mywallet fallback
 * @cmp set_walt_bdgt_hero
 * @events event1
 */
export async function setMyBudgetClick(_ref20, option) {
  let {
    data = {},
    dataLayer = {},
    event
  } = _ref20;
  const {
    text = '',
    withPixallData
  } = data;
  myWalletClickHelper({
    eventResult: `cta::open::${_kebabCase(text)}`,
    withPixallData,
    pixallLabel: 'cta::hero-ad::set-my-budget',
    dataLayer
  }, option);
  return {};
}

/**
 * @summary Sends a Google clickEvent for per month/vehicle selection on Modernize Search Module
 * @cmp bdgt_toggle
 * @events event1
 */
export async function setBudgetSearchFilter(_ref21, option) {
  let {
    data = {},
    event
  } = _ref21;
  const {
    coTxtUrl = ''
  } = data;
  const linkCopy = coTxtUrl === 'monthly' ? 'per-month' : 'vehicle-price';
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'toggle',
    eventResult: `dropdown::${linkCopy}`
  }, option);
  return {};
}

/**
 * @summary Sends a Google clickEvent for edit click on Modernize Search Module
 * @cmp edit_my_walt
 * @events event1
 */
export async function editBudgetSideModule(_ref22, option) {
  let {
    data = {},
    event
  } = _ref22;
  Google.sendClickEvent({
    eventElement: 'my-wallet',
    eventResult: 'text-link::open::edit-my-budget'
  }, option);
  return {};
}

/**
 * @summary Sends a Google clickEvent for 'Shop with Budget' click on Modernize Search Module
 * @cmp 'shop_bdgt'
 * @events event1
 */
export async function shopWithBudgetSideModule(_ref23, option) {
  let {
    data = {},
    event
  } = _ref23;
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventResult: 'cta::srp::shop-with-budget'
  }, option);
  return {};
}

/**
 * @summary Sends a Google impression event when grid component is loaded or in view
 */
export async function walletGridImpression(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    state,
    data
  } = metadata;
  const hasWallet = !!state?.paymentsInfo?.lastUpdated;
  const isInview = data?.isInview;
  Google.sendImpressions(metadata, {
    eventElement: 'my-wallet',
    eventResult: `${isInview ? 'inview::' : ''}my-wallet::${hasWallet ? 'yes' : 'no'}`,
    eventNonInteractive: false
  }, option);
  return {};
}

/**
 * @summary Sends a Google & Pixall clickEvent for 'Verify Credit Range' click on Credit & Term modal and Verify your Credit Range modal
 * @events event1
 */
export async function verifyCreditRangeClick(_ref24, option) {
  let {
    data = {},
    dataLayer = {},
    state,
    paymentsDuck = () => {}
  } = _ref24;
  const {
    par
  } = data;
  const paymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo);
  let consumerMetadata = {};
  const metaData = await getConsumerMetadata();
  if (!_isEmpty(metaData)) {
    consumerMetadata = metaData;
    consumerMetadata.walletId = paymentsInfo?.walletId;
  }
  const {
    brand = ''
  } = state;
  let eventResult;
  let cmp;
  const pixallData = {
    eventSource: 'userAccount',
    feature: 'MyWallet',
    hasListingType: false
  };
  switch (par) {
    case 'verify_crd':
      eventResult = 'cta::verify-credit';
      pixallData.label = 'verify_rng';
      cmp = 'verify_crd';
      break;
    default:
      eventResult = 'cta::verify-your-credit-range';
      pixallData.label = 'verify_crd_rng';
      cmp = brand === 'kbb' ? 'verify_cred_rng' : 'verify_crd_rng';
      break;
  }
  // send GA360 click event
  myWalletClickHelper({
    eventResult
  }, option);

  // send Pixall click event
  Pixall.sendClickEvent({
    ...pixallData,
    label: pixallData.label,
    dealMetadata,
    consumerMetadata,
    tradeinMetadata
  }, dataLayer, option);
  return {
    cmp
  };
}

/**
 * @summary Sends Google Impressions event when user clicks the Verify Credit button and returns to the Credit & Terms modal
 * @events event1
 */
export function verifyCreditRangeMessageImpression(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    data
  } = metadata;
  const {
    status
  } = data;
  let {
    range
  } = data;
  if (range === 'verygood') range = 'very-good';
  Google.sendImpressions(metadata, {
    eventElement: 'my-wallet',
    eventResult: `credit-range::${status}${range ? '::' + range : ''}`
  }, option);
  return {};
}

/**
 * @summary Sends Google and Pixall click event for inline My Wallet click
 * @events event1
 */
export function myWalletGridInlineClick(_ref25, option) {
  let {
    dataLayer = {},
    data = {},
    state = {}
  } = _ref25;
  const {
    enableTitleLink = false,
    inventoryId
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const vehicle = googleInventoryTransformer(inventory, {
    target: 'vehicle',
    dataLayer
  });
  myWalletClickHelper({
    eventResult: 'cta::open::inline-grid'
  }, option);
  Pixall.sendClickEvent({
    ...walletPixallFields,
    label: 'my_walt_grid',
    ...(enableTitleLink && {
      hasListingType: true,
      inventory,
      pixallData: {
        clickTypeToListingType: isVDP(dataLayer)
      },
      cdl: {
        eventProperties: {
          vehicle: getCDLVehicleTransformer(vehicle)
        }
      }
    })
  }, dataLayer, option);
  return {
    cmp: 'my_walt_inline'
  };
}

/**
 * @summary sends Pixall impression event for inline My Wallet trade in indicator
 * @events event1
 */
export async function myWalletGridTradeInIndicator(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    data: {
      isWalletPromoCard = false
    } = {},
    paymentsDuck,
    state
  } = metadata;
  const paymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
  const {
    consumerMetadata,
    dealMetadata,
    tradeinMetadata
  } = await getWalletMetaData(paymentsInfo);
  Pixall.sendImpression(metadata, {
    ...walletPixallFields,
    label: `trade-in-indicator${isWalletPromoCard ? '::inline' : ''}`,
    eventType: 'view',
    ...(isWalletPromoCard && {
      feature: 'myWallet',
      consumerMetadata,
      dealMetadata,
      tradeinMetadata
    })
  }, option);
  return {};
}

/**
 * @summary Sends Google and Pixall click event for contact dealer click from deal
 * @events event32
 */
export function contactDealerClick(_ref26, option) {
  let {
    data = {},
    dataLayer = {},
    event,
    state
  } = _ref26;
  const {
    inventoryId
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const textContent = (event?.target?.innerText || event?.target?.textContent || '').trim();
  const linkCopy = _kebabCase(textContent);
  let label = 'text-link::contact-dealer::my-deals';
  if (linkCopy === 'follow-up') {
    label = 'text-link::follow-up-credit';
  }
  const vehicle = googleInventoryTransformer(inventory, {
    target: 'vehicle',
    dataLayer
  });
  Google.sendClickEvent({
    eventElement: 'my-wallet-deals',
    eventResult: 'cta::email',
    eventData: {
      event: {
        valueEventCode: valueEventCode.LEAD_WITH_MY_WALLET
      },
      vehicle: {
        ...vehicle,
        productConfiguration: 'my-wallet'
      }
    }
  }, option);
  Pixall.sendClickEvent({
    inventory,
    eventSource: 'userAccount',
    eventType: 'click',
    label,
    hasListingType: isVDP(dataLayer),
    feature: isAccelerate ? 'dr3' : 'caca',
    pixallData: {
      clickType: dataLayer?.vehicle?.clickType,
      clickTypeToListingType: isVDP(dataLayer)
    },
    cdl: {
      eventProperties: {
        event: {
          valueEventCode: valueEventCode.LEAD_WITH_MY_WALLET
        },
        vehicle: getCDLVehicleTransformer(vehicle)
      }
    }
  }, dataLayer, option);
  return {};
}

/**
 * @summary Sends Google click event for see details deal click
 * @events event33
 */
export function seeDetailsDealClick(_ref27, option) {
  let {
    data = {},
    dataLayer = {},
    state
  } = _ref27;
  const {
    inventoryId
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  Pixall.sendClickEvent({
    inventory,
    label: 'text-link::see-details::my-deals',
    eventSource: 'userAccount',
    feature: isAccelerate ? 'dr3' : 'caca',
    hasListingType: isVDP(dataLayer),
    pixallData: {
      clickType: dataLayer?.vehicle?.clickType,
      clickTypeToListingType: isVDP(dataLayer)
    },
    cdl: {
      eventProperties: {
        vehicle: getCDLVehicleTransformer(googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        }))
      }
    }
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'my-wallet-deals',
    eventResult: 'text-link::see-details::my-deals'
  }, option);
  return {};
}

/**
 * @summary Sends Google and Pixall click event for Edit this Offer Dealer Dashboard My Wallet
 * @events event49
 */
export function editDealerOfferCTAClick(_ref28, option) {
  let {
    data = {},
    dataLayer = {},
    event,
    state
  } = _ref28;
  const {
    inventoryId,
    isReceiptView
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const textContent = (event?.target?.innerText || event?.target?.textContent || '').trim();
  const linkCopy = _kebabCase(textContent);
  const label = isReceiptView ? 'mod_dl_btn' : `text-link::${linkCopy}`;
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const vehicle = googleInventoryTransformer(inventory, {
    target: 'vehicle',
    dataLayer
  });
  const isVdp = isVDP(dataLayer);
  Pixall.sendClickEvent({
    pageId: DataLayerTransformer.getPageId(dataLayer),
    inventory,
    label,
    eventSource: 'userAccount',
    hasListingType: isVdp,
    feature: isAccelerate ? 'dr3' : 'caca',
    pixallData: {
      clickType: dataLayer?.vehicle?.clickType,
      clickTypeToListingType: isVdp
    },
    cdl: {
      eventProperties: {
        vehicle: getCDLVehicleTransformer(vehicle)
      }
    }
  });
  Google.sendClickEvent({
    eventElement: 'my-wallet-deals',
    eventResult: `cta::${linkCopy}`,
    eventData: {
      vehicle: {
        ...vehicle,
        ...(!isReceiptView && {
          productConfiguration: 'my-wallet'
        })
      }
    }
  }, option);
  return {};
}

/**
 * @summary Sends Google and Pixall click event for text link deal click
 * @events event1
 */
export function editThisOfferClick(_ref29, option) {
  let {
    data = {},
    dataLayer = {},
    event,
    state
  } = _ref29;
  const {
    inventoryId
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const textContent = (event?.target?.innerText || event?.target?.textContent || '').trim();
  const linkCopy = _kebabCase(textContent);
  const label = labelsMap[linkCopy] || 'cta_edit-this-offer';
  Pixall.sendClickEvent({
    inventory,
    label,
    eventSource: 'userAccount',
    feature: 'MyWallet',
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    },
    cdl: {
      eventProperties: {
        vehicle: getCDLVehicleTransformer(googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        }))
      }
    }
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'my-wallet-deals',
    eventResult: `cta::${linkCopy}`,
    eventData: {
      vehicle: {
        productConfiguration: 'my-wallet',
        ...googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        })
      }
    }
  }, option);
  return {};
}

/**
 * @summary Sends Google click event for text link deal click
 * @events event35, event38
 */
export function textLinkDealClick(_ref30, option) {
  let {
    data = {},
    dataLayer = {},
    state = {}
  } = _ref30;
  const {
    text = '',
    placement = '',
    isTestDrive = false,
    inventoryId
  } = data;
  const eventData = {};
  let linkText = _kebabCase(text);
  const placementText = _kebabCase(placement);
  const linkSuffix = placement ? `::${placementText}` : '';
  if (isTestDrive && linkText !== 'schedule-another-visit') {
    linkText = linkText.concat('-test-drive');
  }
  const pixallLabel = labelsMap[placementText] || labelsMap[linkText];
  if (pixallLabel) {
    const inventory = getInventoryOwnerById(state, inventoryId);
    const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
    const vehicle = googleInventoryTransformer(inventory, {
      target: 'vehicle',
      dataLayer
    });
    eventData.vehicle = vehicle;
    Pixall.sendClickEvent({
      inventory,
      label: pixallLabel,
      eventSource: 'userAccount',
      feature: isAccelerate ? 'dr3' : 'caca',
      hasListingType: isVDP(dataLayer),
      // 'lt' only be shown on VDP and the value will be the clickType as below
      pixallData: {
        clickType: dataLayer?.vehicle?.clickType,
        clickTypeToListingType: isVDP(dataLayer)
      },
      cdl: {
        eventProperties: {
          vehicle: getCDLVehicleTransformer(vehicle)
        }
      }
    }, dataLayer, option);
  }
  Google.sendClickEvent({
    eventElement: 'my-wallet-deals',
    eventResult: `text-link::${linkText}${linkSuffix}`,
    eventData
  }, option);
  return {};
}

/**
 * @summary Sends Google click event for credit approval deal click
 * @events event34, event36
 */
export function creditPreApprovalClick(_ref31, option) {
  let {
    data = {},
    dataLayer = {},
    state = {}
  } = _ref31;
  const {
    text = '',
    partnerType = '',
    isReceiptView,
    inventoryId
  } = data;
  const linkData = _kebabCase(text).concat(partnerType ? '' : '-credit');
  const eventResult = `text-link::${partnerType ? `caca-${partnerType}::${linkData}` : linkData}`;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const vehicle = googleInventoryTransformer(inventory, {
    target: 'vehicle',
    dataLayer
  });
  if (isReceiptView) {
    Pixall.sendClickEvent({
      inventory,
      label: 'apply_crd',
      eventSource: 'userAccount',
      feature: 'dr3',
      pixallData: {
        clickTypeToListingType: isVDP(dataLayer)
      },
      cdl: {
        eventProperties: {
          vehicle: getCDLVehicleTransformer(vehicle)
        }
      }
    }, dataLayer, option);
  }
  Google.sendClickEvent({
    eventElement: 'my-wallet-deals',
    eventResult
  }, option);
  return {};
}

/**
 * @summary Sends Google and Pixall click event for credit approval followup click
 * @events event34, event36
 */
export function creditAppFollowupClick(_ref32, option) {
  let {
    data = {},
    dataLayer = {},
    state
  } = _ref32;
  const {
    text = '',
    inventoryId
  } = data;
  const linkData = _kebabCase(text).concat('-credit');
  const eventResult = `text-link::${linkData}`;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const vehicle = googleInventoryTransformer(inventory, {
    target: 'vehicle',
    dataLayer
  });
  Google.sendClickEvent({
    eventElement: 'my-wallet-deals',
    eventResult
  }, option);
  Pixall.sendClickEvent({
    inventory,
    eventSource: 'userAccount',
    eventType: 'click',
    label: eventResult,
    hasListingType: false,
    feature: isAccelerate ? 'dr3' : 'caca',
    cdl: {
      eventProperties: {
        vehicle: getCDLVehicleTransformer(vehicle)
      }
    }
  }, dataLayer, option);
  return {};
}
export function seeDetailsGlobalNavDealImpression(metaData, option) {
  const {
    data = {}
  } = metaData;
  const {
    location = ''
  } = data;
  Pixall.sendImpression(metaData, {
    ...walletPixallFields,
    label: `text-link::see-details::${location}`,
    eventType: 'view'
  }, option);
  Google.sendImpressions(metaData, {
    eventElement: 'my-wallet',
    eventResult: `text-link::see-details::${location}`
  }, option);
  return {};
}

/**
 * @summary Sends Google click event for see details my global nav click
 * @events event39
 */
export function seeDetailsGlobalNavDealClick(_ref33, option) {
  let {
    data = {},
    dataLayer = {}
  } = _ref33;
  const {
    location = ''
  } = data;
  Pixall.sendClickEvent({
    ...walletPixallFields,
    label: `text-link::see-details::${location}`
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'my-wallet',
    eventResult: `text-link::see-details::${location}`
  }, option);
  return {};
}

/**
 * @summary Sends Google click event for see details my deal click
 * @events event40
 */
export function seeDetailsMyWalletDealClick(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  Google.sendClickEvent({
    eventElement: 'my-wallet',
    eventResult: 'text-link::see-details::my-wallet'
  }, option);
  return {};
}
export function getPayoffQuoteFromLenderClick(_ref34, option) {
  let {
    data = {},
    dataLayer = {}
  } = _ref34;
  const textContent = sanitizeValue(_get(data, 'textContent', ''));
  Pixall.sendClickEvent({
    ...walletPixallFields,
    label: 'get_payoff_qt_lndr'
  }, dataLayer, option);
  myWalletClickHelper({
    eventResult: `text-link::${textContent}`
  }, option);
  return {};
}
export function getPayoffQuoteSubmitClick(_ref35, option) {
  let {
    data = {},
    dataLayer = {}
  } = _ref35;
  const textContent = sanitizeValue(_get(data, 'textContent', ''));
  Pixall.sendClickEvent({
    ...walletPixallFields,
    label: 'get_payoff_qt'
  }, dataLayer, option);
  myWalletClickHelper({
    eventResult: `cta::${textContent}`
  }, option);
  return {};
}
export function paymentOptionClick(_ref36, option) {
  let {
    data = {},
    dataLayer = {}
  } = _ref36;
  const paymentOption = sanitizeValue(_get(data, 'paymentOption', ''));
  Pixall.sendClickEvent({
    ...walletPixallFields,
    label: `show_${paymentOption}`
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'my-wallet',
    eventResult: `toggle::${paymentOption}`
  }, option);
  return {};
}

/**
 * @summary Sends a Pixall clickEvent for Engagement
 * @cmp my-wallet_...
 * @events event1
 */
export async function myWalletEngagementClick(_ref37, option) {
  let {
    data = {},
    dataLayer = {},
    state
  } = _ref37;
  const {
    panel = '',
    paymentsDuck
  } = data;
  let budgetType = '';
  switch (panel) {
    case 'creditScore':
      budgetType = 'term';
      break;
    default:
      // convert to kebab-case
      budgetType = `${_kebabCase(panel)}`;
  }
  const label = `my-wallet_${budgetType}`;
  const paymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo);
  let consumerMetadata = {};
  const metaData = await getConsumerMetadata();
  if (!_isEmpty(metaData)) {
    consumerMetadata = metaData;
    consumerMetadata.walletId = paymentsInfo?.walletId;
  }
  myWalletClickHelper({
    dataLayer,
    withPixallData: true,
    pixallLabel: label,
    pixallData: {
      dealMetadata,
      tradeinMetadata,
      consumerMetadata
    }
  }, option);
  return {
    label
  };
}

/**
 * @summary Sends Pixall click event for Search Available Vehicles - Click
 * @events event7
 */
export async function searchAvailableVehiclesClick(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    data = {},
    dataLayer,
    state
  } = metadata;
  const {
    isWalletPromoCard = false,
    eventResult = '',
    paymentsDuck
  } = data;
  const paymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
  const {
    consumerMetadata,
    dealMetadata,
    tradeinMetadata
  } = await getWalletMetaData(paymentsInfo);
  let pixallLabel = 'cta::search-available-vehicles';
  if (isWalletPromoCard) {
    pixallLabel += '::inline';
  }
  if (eventResult) {
    pixallLabel = eventResult;
  }
  myWalletClickHelper({
    data,
    eventResult: isWalletPromoCard && 'cta::search-available-vehicles',
    pixallLabel,
    withPixallData: true,
    dataLayer,
    pixallData: {
      dealMetadata,
      consumerMetadata,
      tradeinMetadata
    }
  }, option);
  return {};
}

/**
 * @summary Sends Pixall click event for My Wallet > My Deals > See Details - Click
 * @events event8
 */
export function myDealSeeDetailsClick(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    data,
    state,
    dataLayer
  } = metadata;
  const {
    inventoryId
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const vehicle = googleInventoryTransformer(inventory, {
    target: 'vehicle',
    dataLayer
  });
  myWalletClickHelper({
    ...metadata?.data,
    pixallLabel: 'text-link::see-details::my-wallet',
    withPixallData: true,
    dataLayer,
    pixallData: {
      cdl: {
        eventProperties: {
          vehicle: getCDLVehicleTransformer(vehicle)
        }
      }
    }
  }, option);
  return {};
}

/**
 * @summary Sends Pixall click event for KBB Trade-In Value on Another Vehicle - Click
 * @events event11
 */
export function myWalletTradeInValueAnotherVehicleClick(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    dataLayer
  } = metadata;
  myWalletClickHelper({
    ...metadata?.data,
    pixallLabel: 'text-link::my-wallet::get-a-value-for-another-vehicle',
    withPixallData: true,
    dataLayer
  }, option);
  return {};
}

/**
 * @summary Sends GA click event for Wallet Email Capture - Click
 * @events event34
 */
export function walletEmailCaptureSubmit(metaData, option) {
  if (metaData === void 0) {
    metaData = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    dataLayer = {}
  } = metaData;
  myWalletClickHelper({
    eventResult: 'cta::alert::subscribe::email',
    dataLayer
  }, option);
  return {};
}

/**
 * @summary Sends GA click event for MyWallet icon button on top of the SRP - Click
 * @events event14
 */
export function myWalletIconButtonClick(metaData, option) {
  if (metaData === void 0) {
    metaData = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    dataLayer = {}
  } = metaData;
  myWalletClickHelper({
    eventResult: 'text-link::open::show-payment',
    dataLayer,
    withPixallData: true,
    pixallLabel: 'my_walt'
  }, option);
  return {};
}
export async function offsiteBrowserPushNotificationImpression(metaData, option) {
  if (metaData === void 0) {
    metaData = {};
  }
  const {
    data = {},
    paymentsDuck,
    state,
    dataLayer
  } = metaData;
  const {
    location
  } = data;
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const label = pageId !== 'fyc_srl' && location === 'srp' ? `toggle::push-notification::${pageId}` : `toggle::push-notification::${location}`;
  const eventElement = location === 'srp' ? 'notification' : location;
  const pixallData = await getOffsitePixallData(data, paymentsDuck, state, true);
  const pixallImpressionData = {
    ...pixallData,
    label
  };
  if (location === 'vdp') {
    pixallImpressionData.includeInventory = true;
    pixallImpressionData.clickType = pixallData?.inventory?.clickType;
  }
  Pixall.sendImpression(metaData, pixallImpressionData, option);
  Google.sendImpressions(metaData, {
    eventElement,
    eventResult: label
  }, option);
  return {};
}
export async function offsiteBrowserPushNotificationClick(_ref38, option) {
  let {
    data = {},
    dataLayer = {},
    paymentsDuck,
    state
  } = _ref38;
  if (option === void 0) {
    option = {};
  }
  const {
    location
  } = data;
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const label = pageId !== 'fyc_srl' && location === 'srp' ? `toggle::on::push-notification::${pageId}` : `toggle::on::push-notification::${location}`;
  const eventElement = location === 'srp' ? 'notification' : location;
  const pixallData = await getOffsitePixallData(data, paymentsDuck, state);
  Google.sendClickEvent({
    eventElement,
    eventResult: label
  }, option);
  Pixall.sendClickEvent({
    label,
    ...pixallData
  }, dataLayer, option);
  return {};
}

/**
 * @summary Sends a Google and Pixall click event for PSX cta on the trade in panel
 * @events event1
 */
export async function myWalletSellYourCarClick(_temp, option) {
  let {
    data = {},
    dataLayer = {},
    state
  } = _temp === void 0 ? {} : _temp;
  const {
    paymentsDuck
  } = data;
  const paymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
  const {
    consumerMetadata,
    dealMetadata,
    tradeinMetadata
  } = await getWalletMetaData(paymentsInfo);
  const pixallData = {
    consumerMetadata,
    dealMetadata,
    tradeinMetadata
  };
  const eventResult = 'text-link::slp_cen::sell-your-car';
  myWalletClickHelper({
    eventResult,
    withPixallData: true,
    pixallLabel: eventResult,
    dataLayer,
    pixallData
  }, option);
  return {};
}
export async function walletPromoCardLinkClick(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    data,
    dataLayer,
    state
  } = metadata;
  const {
    paymentsDuck
  } = data;
  const text = sanitizeValue(_get(data, 'text', '').trim());
  const paymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
  const {
    consumerMetadata,
    dealMetadata,
    tradeinMetadata
  } = await getWalletMetaData(paymentsInfo);
  myWalletClickHelper({
    withPixallData: true,
    pixallLabel: `text-link::${text}::inline`,
    dataLayer,
    pixallData: {
      consumerMetadata,
      dealMetadata,
      tradeinMetadata
    }
  }, option);
  return {};
}
export async function walletDropdownEngage(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    data,
    dataLayer,
    state
  } = metadata;
  const {
    label,
    paymentsDuck
  } = data;
  const paymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
  const {
    consumerMetadata,
    dealMetadata,
    tradeinMetadata
  } = await getWalletMetaData(paymentsInfo);
  myWalletClickHelper({
    withPixallData: true,
    pixallLabel: label,
    dataLayer,
    pixallData: {
      consumerMetadata,
      dealMetadata,
      tradeinMetadata
    }
  }, option);
  return {};
}
export async function walletGridVerifyCreditClick(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    dataLayer
  } = metadata;
  myWalletClickHelper({
    withPixallData: true,
    pixallLabel: 'text-link::verify-your-credit',
    dataLayer
  }, option);
  return {};
}
export async function walletSaveOrSyncYourShoppingEntryPoint(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    useCDL = false
  } = option;
  const {
    data = {},
    dataLayer
  } = metadata;
  const {
    label = ''
  } = data;
  const textContent = sanitizeValue(label);
  Pixall.sendClickEvent({
    cdl: useCDL ? {
      eventProperties: {
        event: {
          element: 'Sync Your Shopping CTA',
          action: 'Click',
          intention: 'Navigate to Sync your Shopping Modal',
          location: 'My Wallet',
          elementText: textContent
        }
      }
    } : undefined
  }, dataLayer, option);
  return {};
}
export default {
  myWalletOpenPageLoad,
  myWalletFilterToggleClick,
  myWalletMonthlyPaymentFilterClick,
  myWalletSearchByBudgetClick,
  myWalletSummaryLinkClick,
  myWalletCalculatePaymentsCTAClick,
  paymentDetailsMakeChangesClick,
  myWalletGetStartedClick,
  browseByBudgetWalletContinueClick,
  snapshotPaymentDetailsClick,
  myWalletSearchAvailableVehiclesSubmit,
  myWalletSellYourCarClick,
  myWalletTradeInClickLink,
  myWalletTradeInSubmit,
  myWalletIcoLinkClick,
  myWalletInputChange,
  myWalletInteractionClick,
  myWalletReturnToVdpCtaClick,
  myWalletMobileToolbarClick,
  myWalletNonDealNotificationsImpression,
  myWalletNonDealNotificationsClick,
  myWalletCarouselBudgetClick,
  myWalletCheckDealStatusCTAClick,
  paymentDetailsVehicleCTAClick,
  applyValueToWalletClick,
  myWalletKbbTradeInIndicator,
  myWalletDealBannerImpression,
  myWalletViewDealStatusCTAClick,
  setMyBudgetClick,
  setBudgetSearchFilter,
  editBudgetSideModule,
  shopWithBudgetSideModule,
  walletGridImpression,
  verifyCreditRangeClick,
  verifyCreditRangeMessageImpression,
  myWalletGridInlineClick,
  myWalletGridTradeInIndicator,
  contactDealerClick,
  seeDetailsDealClick,
  editDealerOfferCTAClick,
  editThisOfferClick,
  textLinkDealClick,
  creditPreApprovalClick,
  creditAppFollowupClick,
  seeDetailsMyWalletDealClick,
  seeDetailsGlobalNavDealImpression,
  seeDetailsGlobalNavDealClick,
  getPayoffQuoteFromLenderClick,
  getPayoffQuoteSubmitClick,
  paymentOptionClick,
  myWalletEngagementClick,
  searchAvailableVehiclesClick,
  myDealSeeDetailsClick,
  myWalletTradeInValueAnotherVehicleClick,
  walletEmailCaptureSubmit,
  myWalletIconButtonClick,
  offsiteBrowserPushNotificationImpression,
  offsiteBrowserPushNotificationClick,
  walletGridVerifyCreditClick,
  walletDropdownEngage,
  walletPromoCardLinkClick,
  walletSaveOrSyncYourShoppingEntryPoint
};